import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useApiHeaders } from '../../hooks/useApiHeaders';

interface Preference {
  applied: boolean;
  dismissed: boolean;
}

interface Snapshot extends Preference {
  ready: boolean;
  error: string | null;
}

interface Entry {
  botId: string;
  snapshot: Snapshot;
  listeners: Set<() => void>;
  headers: () => Record<string, string>;
  readVersion: number;
  reading?: Promise<void>;
}

const EMPTY: Snapshot = { applied: false, dismissed: false, ready: false, error: null };
const entries = new Map<string, Entry>();
const activeEntries = new Set<Entry>();
const CHANGE_KEY = 'voxcore:hebrew-setup-preference:changed';

function entryFor(botId: string): Entry {
  let entry = entries.get(botId);
  if (!entry) {
    entry = { botId, snapshot: EMPTY, listeners: new Set(), headers: () => ({}), readVersion: 0 };
    entries.set(botId, entry);
  }
  return entry;
}

function update(entry: Entry, patch: Partial<Snapshot>) {
  entry.snapshot = {
    ...entry.snapshot,
    ...patch,
    // A delayed read or another entry point must never undo a saved decision.
    applied: entry.snapshot.applied || patch.applied === true,
    dismissed: entry.snapshot.dismissed || patch.dismissed === true,
  };
  entry.listeners.forEach(listener => listener());
}

async function readResponse(response: Response): Promise<Preference> {
  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || 'Could not save or load your Hebrew template preference. Please try again.');
  }
  const value = await response.json();
  if (!value || typeof value.applied !== 'boolean' || typeof value.dismissed !== 'boolean') {
    throw new Error('Could not load your Hebrew template preference. Please try again.');
  }
  return value;
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : 'Could not load your Hebrew template preference. Please try again.';
}

function load(entry: Entry, invalidate = false): Promise<void> {
  if (entry.reading && !invalidate) return entry.reading;
  const version = ++entry.readVersion;
  // Suppress automatic offers until the server has confirmed their eligibility.
  update(entry, { ready: false, error: null });
  const request = (async () => {
    try {
      const response = await fetch(`/api/bot/${encodeURIComponent(entry.botId)}/hebrew-setup-preference`, {
        headers: entry.headers(),
      });
      const preference = await readResponse(response);
      update(entry, version === entry.readVersion ? { ...preference, ready: true, error: null } : preference);
    } catch (error) {
      if (version === entry.readVersion) update(entry, { ready: false, error: messageFor(error) });
    }
  })();
  entry.reading = request;
  void request.finally(() => {
    if (entry.reading === request) entry.reading = undefined;
  });
  return request;
}

function onFocus() {
  activeEntries.forEach(entry => { void load(entry, true); });
}

function onStorage(event: StorageEvent) {
  if (event.key !== CHANGE_KEY || !event.newValue) return;
  try {
    const { botId } = JSON.parse(event.newValue);
    const entry = typeof botId === 'string' ? entries.get(botId) : undefined;
    if (entry && activeEntries.has(entry)) void load(entry, true);
  } catch {
    // This is only a notification; preference values always come from the API.
  }
}

function subscribe(entry: Entry, listener: () => void) {
  entry.listeners.add(listener);
  if (activeEntries.size === 0) {
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
  }
  activeEntries.add(entry);
  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size === 0) {
      activeEntries.delete(entry);
      entry.snapshot = { ...entry.snapshot, ready: false };
    }
    if (activeEntries.size === 0) {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    }
  };
}

async function save(entry: Entry | undefined, field: keyof Preference): Promise<void> {
  if (!entry) throw new Error('Select a bot before saving your Hebrew template preference.');
  try {
    const response = await fetch(`/api/bot/${encodeURIComponent(entry.botId)}/hebrew-setup-preference`, {
      method: 'PUT',
      headers: entry.headers(),
      body: JSON.stringify({ [field]: true }),
    });
    const preference = await readResponse(response);
    if (!preference[field]) throw new Error('Could not save your Hebrew template preference. Please try again.');
    ++entry.readVersion;
    update(entry, { ...preference, ready: true, error: null });
    try {
      window.localStorage.setItem(CHANGE_KEY, JSON.stringify({ botId: entry.botId, nonce: `${Date.now()}-${Math.random()}` }));
    } catch {
      // The server already saved the decision; blocked browser storage is harmless.
    }
  } catch (error) {
    update(entry, { error: messageFor(error) });
    throw error;
  }
}

/** Shared, bot-scoped server preference; it is independent of draft/publish state. */
export function useHebrewSetupPreference(botId?: string) {
  const apiHeaders = useApiHeaders();
  const headersRef = useRef(apiHeaders);
  headersRef.current = apiHeaders;
  const entry = botId ? entryFor(botId) : undefined;
  if (entry) entry.headers = () => headersRef.current();

  const subscribeToEntry = useCallback((listener: () => void) => entry ? subscribe(entry, listener) : () => {}, [entry]);
  const getSnapshot = useCallback(() => entry?.snapshot || EMPTY, [entry]);
  const snapshot = useSyncExternalStore(subscribeToEntry, getSnapshot, () => EMPTY);
  const refresh = useCallback(async () => { if (entry) await load(entry); }, [entry]);
  const markApplied = useCallback(() => save(entry, 'applied'), [entry]);
  const dismiss = useCallback(() => save(entry, 'dismissed'), [entry]);

  useEffect(() => {
    // Another mounted entry point already has the same server-verified state.
    // The last unsubscribe invalidates readiness, so a later remount revalidates.
    if (entry && !entry.snapshot.ready) void refresh();
  }, [entry, refresh]);

  return {
    ready: snapshot.ready,
    suppressed: !snapshot.ready || snapshot.applied || snapshot.dismissed,
    error: snapshot.error,
    markApplied,
    dismiss,
    refresh,
  };
}
